export const MISSING_CLONALITY_COMPARISON_MESSAGE = 'These project samples do not have completed clonality comparison results, so they are not shown in the clonality matrix. Clonality analysis only considers samples with a positive, supported pathogen. Recently uploaded samples may appear after the next clonality analysis cycle.';

const getAttributes = (libSeq) => libSeq?.attributes || {};

const getLibSeqId = (libSeq, attributes) => (
  attributes.lib_seq_id ?? attributes.id ?? libSeq?.id ?? null
);

const parsePathFromUrl = (urlValue) => {
  const normalizedUrl = String(urlValue || '').trim();
  if (!normalizedUrl) {
    return null;
  }

  if (normalizedUrl.startsWith('/')) {
    return normalizedUrl;
  }

  try {
    const parsedUrl = new URL(normalizedUrl);
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (error) {
    return null;
  }
};

export const getSampleLink = (attributes = {}, activeProjectUuid) => {
  const pathFromUrl = parsePathFromUrl(attributes.keynome_web_url);
  if (pathFromUrl) {
    return pathFromUrl;
  }

  const sampleUuid = attributes.sample_uuid || attributes.uuid;
  if (activeProjectUuid && attributes.sequencing_run_uuid && sampleUuid) {
    return `/projects/${activeProjectUuid}/runs/${attributes.sequencing_run_uuid}/samples/${sampleUuid}`;
  }

  return null;
};

export const getMissingClonalitySampleLabel = ({ sampleName, libSeqId } = {}) => {
  const normalizedSampleName = String(sampleName || '').trim();
  if (normalizedSampleName) {
    return normalizedSampleName;
  }

  if (libSeqId != null) {
    return `Sample ${libSeqId}`;
  }

  return 'Unknown sample';
};

const getDatasetName = (attributes) => (
  attributes.dataset_name || attributes.sequencing_run_name || attributes.run_name || ''
);

const getOrganism = (attributes) => (
  attributes.identified_organism || attributes.organism || ''
);

const normalizeLibSeqsResponse = (libSeqsResponse) => {
  if (Array.isArray(libSeqsResponse)) {
    return libSeqsResponse;
  }

  if (Array.isArray(libSeqsResponse?.data)) {
    return libSeqsResponse.data;
  }

  return [];
};

export const sortMissingClonalitySamples = (samples) => [...(samples || [])].sort((left, right) => {
  const datasetCompare = left.datasetName.localeCompare(right.datasetName);
  if (datasetCompare !== 0) {
    return datasetCompare;
  }

  const sampleCompare = left.sampleName.localeCompare(right.sampleName);
  if (sampleCompare !== 0) {
    return sampleCompare;
  }

  return String(left.key).localeCompare(String(right.key));
});

export const getMissingClonalitySamples = (
  libSeqsResponse,
  { activeProjectUuid } = {}
) => {
  const rowsByKey = new Map();

  normalizeLibSeqsResponse(libSeqsResponse).forEach((libSeq) => {
    const attributes = getAttributes(libSeq);
    const libSeqId = getLibSeqId(libSeq, attributes);
    const key = libSeqId != null ? `lib-seq-${libSeqId}` : `lib-seq-row-${libSeq?.id || rowsByKey.size}`;
    const sampleName = getMissingClonalitySampleLabel({
      sampleName: attributes.sample_name || attributes.name,
      libSeqId
    });

    if (rowsByKey.has(key)) {
      return;
    }

    rowsByKey.set(key, {
      key,
      libSeqId,
      sampleName,
      datasetName: getDatasetName(attributes),
      organism: getOrganism(attributes),
      analysisStatus: attributes.analysis_status || '',
      analysisExecutionCompletedAt: attributes.analysis_execution_completed_at || '',
      samplePath: getSampleLink(attributes, activeProjectUuid)
    });
  });

  return sortMissingClonalitySamples([...rowsByKey.values()]);
};
